import { apiClient } from '@api'

/**
 * Package Service
 *
 * Service layer for managing subscription packages
 * Provides methods for CRUD operations on packages
 */
class PackageService {
	/**
	 * Get all packages
	 * @param {Object} params - Query parameters (page, limit, status, search)
	 * @returns {Promise<Object>} Packages data with pagination meta
	 */
	async getAll(params = {}) {
		const queryParams = new URLSearchParams()

		if (params.page) queryParams.append('page', params.page)
		if (params.limit) queryParams.append('limit', params.limit)
		if (params.status) queryParams.append('status', params.status)
		if (params.search) queryParams.append('search', params.search)

		const queryString = queryParams.toString()
		const endpoint = queryString ? `/packages?${queryString}` : '/packages'

		return await apiClient.get(endpoint)
	}

	/**
	 * Get package by ID
	 * @param {string|number} id - Package ID
	 * @returns {Promise<Object>} Package data
	 */
	async getById(id) {
		return await apiClient.get(`/packages/${id}`)
	}

	/**
	 * Get package by slug
	 * @param {string} slug - Package slug
	 * @returns {Promise<Object>} Package data
	 */
	async getBySlug(slug) {
		return await apiClient.get(`/packages/slug/${slug}`)
	}

	/**
	 * Create new package
	 * @param {Object} packageData - Package data
	 * @returns {Promise<Object>} Created package data
	 */
	async create(packageData) {
		return await apiClient.post('/packages', packageData)
	}

	/**
	 * Update package
	 * @param {string|number} id - Package ID
	 * @param {Object} packageData - Updated package data
	 * @returns {Promise<Object>} Updated package data
	 */
	async update(id, packageData) {
		return await apiClient.put(`/packages/${id}`, packageData)
	}

	/**
	 * Archive package (status only)
	 * @param {string|number} id - Package ID
	 * @returns {Promise<Object>} Archived package data
	 */
	async archive(id) {
		return await apiClient.post(`/packages/${id}/archive`)
	}

	/**
	 * Delete package (soft delete with deleted_at)
	 * @param {string|number} id - Package ID
	 * @returns {Promise<Object>} Success response
	 */
	async delete(id) {
		return await apiClient.delete(`/packages/${id}`)
	}

	/**
	 * Restore archived package
	 * @param {string|number} id - Package ID
	 * @returns {Promise<Object>} Restored package data
	 */
	async restore(id) {
		return await apiClient.patch(`/packages/${id}/restore`)
	}

	/**
	 * Get active packages only
	 * @returns {Promise<Object>} Active packages data
	 */
	async getActive() {
		return await apiClient.get('/packages/active')
	}
}

// Export singleton instance
export const packageService = new PackageService()
export default packageService
